import type {
  MenuItem,
  MenuLink,
} from "core-fe-lib/components-shadcn/types/menu-link";

/**
 * Hub-side menu grouping.
 *
 * Each module returns its own top-level `MenuLink` (one expandable section per
 * module). To wrap several modules under a single parent section, declare a
 * group below — `groupMenuLinks` then transforms the flat list into a nested
 * one for the sidebar.
 *
 * Unmatched modules pass through in registration order. Grouped modules are
 * removed from their original spots and the group is positioned per
 * `placement` — by default at the end (just above the Administration trailing
 * section).
 */
export interface MenuGroup {
  /** Stable id for the group (used to translate title/caption). */
  id: string;
  /** Material-icon name resolved via the hub icon map. */
  icon: string;
  /** Module ids (matching `HubModule.id`) to fold under this group. */
  moduleIds: string[];
  /** Where to place the grouped section. Default: "end". */
  placement?: "first-match" | "end";
}

export function getMenuGroups(): MenuGroup[] {
  return [
    {
      id: "operations",
      icon: "engineering",
      moduleIds: ["cron", "outbox", "secret", "pipeline", "automation"],
      placement: "end",
    },
  ];
}

export function makeMenuLinksTransform(
  groups: MenuGroup[],
  t: (key: string) => string
): (links: MenuLink[]) => MenuLink[] {
  return (links) => applyGroups(links, groups, t);
}

function applyGroups(
  links: MenuLink[],
  groups: MenuGroup[],
  t: (key: string) => string
): MenuLink[] {
  if (!groups.length) return links;

  const groupByModule = new Map<string, MenuGroup>();
  for (const g of groups) {
    for (const id of g.moduleIds) groupByModule.set(id, g);
  }

  const collected = new Map<string, MenuLink[]>();
  const out: (MenuLink | { __groupId: string })[] = [];
  const placed = new Set<string>();
  const endGroups: string[] = [];

  for (const link of links) {
    const moduleId = link.moduleId;
    const group = moduleId ? groupByModule.get(moduleId) : undefined;
    if (!group) {
      out.push(link);
      continue;
    }
    const bucket = collected.get(group.id) ?? [];
    bucket.push(link);
    collected.set(group.id, bucket);
    if (!placed.has(group.id)) {
      placed.add(group.id);
      if ((group.placement ?? "end") === "end") {
        endGroups.push(group.id);
      } else {
        out.push({ __groupId: group.id });
      }
    }
  }

  for (const id of endGroups) out.push({ __groupId: id });

  return out
    .map((entry) => {
      if (!("__groupId" in entry)) return entry;
      const group = groups.find((g) => g.id === entry.__groupId);
      const members = collected.get(entry.__groupId) ?? [];
      if (!group || !members.length) return null;
      return {
        title: t(`layout.navigation.${group.id}.title`),
        caption: t(`layout.navigation.${group.id}.caption`),
        icon: group.icon,
        items: members.map(linkToItem),
      } as MenuLink;
    })
    .filter((x): x is MenuLink => x !== null);
}

function linkToItem(link: MenuLink): MenuItem {
  return {
    title: link.title,
    caption: link.caption,
    icon: link.icon,
    items: link.items,
  };
}
